import { useState } from 'react';
import { EMAIL_ENABLED } from './features';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, command, getObject, mutationHeaders, type Schema } from './api/client';
import { apiError } from './api/session';
import { Button, Empty, ErrorState, Field, Loading, Status, useToast } from './ui';

const categories: Record<string, string> = {
  ADMINISTRATIVE: 'Сроки ресурсов и оплаты',
  PERFORMANCE: 'Оценка и рабочие планы',
  SECURITY_LOGIN: 'Вход в учётную запись',
  SECURITY_ACCOUNT: 'Безопасность учётной записи',
  ACCESS: 'Изменение доступа',
  HR_PROFILE: 'Профиль сотрудника',
  RECRUITMENT: 'Подбор персонала',
};
export function NotificationSettingsPage() {
  const client = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<Error | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const preferences = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: ({ signal }) =>
      getObject<{ items: Schema<'NotificationPreferenceResponse'>[] }>(
        '/api/v1/notification-preferences',
        {},
        signal,
      ),
  });
  if (preferences.isLoading) return <Loading />;
  if (preferences.error) return <ErrorState error={preferences.error} />;
  const update = async (
    item: Schema<'NotificationPreferenceResponse'>,
    key: 'emailEnabled' | 'inAppEnabled',
    value: boolean,
  ) => {
    setPending(item.category);
    setError(null);
    try {
      await command(
        'PUT',
        '/api/v1/notification-preferences/{category}',
        { emailEnabled: item.emailEnabled, inAppEnabled: item.inAppEnabled, [key]: value },
        { category: item.category },
      );
      await client.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast('Настройки сохранены');
    } catch (cause) {
      setError(cause as Error);
    } finally {
      setPending(null);
    }
  };
  return (
    <section className="settings-page">
      <h1>Уведомления</h1>
      <div className="panel settings-card">
        <header>
          <h2>Каналы доставки</h2>
        </header>
        <div className={`preference-header ${EMAIL_ENABLED ? '' : 'without-email'}`}>
          <span>Категория</span>
          {EMAIL_ENABLED && <span>Почта</span>}
          <span>В приложении</span>
        </div>
        {preferences.data?.items.map((item) => (
          <div
            className={`preference-row ${EMAIL_ENABLED ? '' : 'without-email'}`}
            key={item.category}
          >
            <div>
              <strong>{categories[item.category] || item.category}</strong>
              {item.mandatory && <p className="muted">Обязательные уведомления</p>}
            </div>
            {EMAIL_ENABLED && (
              <label>
                <span className="sr-only">{categories[item.category]}: почта</span>
                <input
                  type="checkbox"
                  checked={item.emailEnabled}
                  disabled={item.mandatory || pending === item.category}
                  onChange={(event) => void update(item, 'emailEnabled', event.target.checked)}
                />
              </label>
            )}
            <label>
              <span className="sr-only">{categories[item.category]}: в приложении</span>
              <input
                type="checkbox"
                checked={item.inAppEnabled}
                disabled={item.mandatory || pending === item.category}
                onChange={(event) => void update(item, 'inAppEnabled', event.target.checked)}
              />
            </label>
          </div>
        ))}
      </div>
      {error && <ErrorState error={error} />}
      <Link className="pill" to="/account/settings">
        Безопасность учётной записи
      </Link>
    </section>
  );
}

export function MfaEnrollmentPage() {
  const path = useRouterState({ select: (state) => state.location.pathname });
  const method = path.split('/').at(-1) === 'email' ? 'EMAIL' : 'TOTP';
  const [enrollment, setEnrollment] = useState<
    Schema<'MfaEmailEnrollmentResponse'> | Schema<'MfaTotpEnrollmentResponse'> | null
  >(null);
  const [pending, setPending] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const client = useQueryClient();
  const begin = async () => {
    setPending(true);
    setError(null);
    try {
      const result =
        method === 'EMAIL'
          ? await api.POST('/api/v1/auth/mfa/email/enrollment', {
              params: { header: mutationHeaders() },
            })
          : await api.POST('/api/v1/auth/mfa/totp/enrollment');
      if (!result.data) throw apiError(result.response, result.error);
      setEnrollment(result.data);
    } catch (cause) {
      setError(cause as Error);
    } finally {
      setPending(false);
    }
  };
  if (method === 'EMAIL' && !EMAIL_ENABLED)
    return (
      <Empty
        title="Почтовое подтверждение отключено"
        subtitle="Используйте приложение-аутентификатор"
      />
    );
  return (
    <section className="form-page">
      <div className="form-page-heading">
        <h1>{method === 'EMAIL' ? 'Подтверждение входа по почте' : 'Приложение-аутентификатор'}</h1>
      </div>
      {complete ? (
        <>
          <Empty title="Способ подтверждения подключён" />
          <Link className="pill primary" to="/account/settings">
            Вернуться к настройкам
          </Link>
        </>
      ) : (
        <form
          className="domain-form"
          onSubmit={async (event) => {
            event.preventDefault();
            setPending(true);
            setError(null);
            try {
              const code = String(new FormData(event.currentTarget).get('code'));
              const result =
                method === 'EMAIL'
                  ? await api.POST('/api/v1/auth/mfa/email/enrollment/verify', {
                      body: { code },
                      params: { header: mutationHeaders() },
                    })
                  : await api.POST('/api/v1/auth/mfa/totp/enrollment/verify', {
                      body: { code },
                      params: { header: mutationHeaders() },
                    });
              if (result.error) throw apiError(result.response, result.error);
              setEnrollment(null);
              setComplete(true);
              void client.invalidateQueries({ queryKey: ['mfa-status'] });
            } catch (cause) {
              setError(cause as Error);
            } finally {
              setPending(false);
            }
          }}
        >
          {!enrollment ? (
            <>
              <p className="muted">
                {method === 'EMAIL'
                  ? 'Код подтверждения придёт на вашу рабочую почту.'
                  : 'Подключите приложение-аутентификатор и подтвердите настройку шестизначным кодом.'}
              </p>
              <Button onClick={() => void begin()} className="primary" disabled={pending}>
                {pending ? 'Подождите…' : 'Начать настройку'}
              </Button>
            </>
          ) : (
            <>
              {enrollment.method === 'TOTP' ? (
                <div className="panel settings-card">
                  <h2>Добавьте учётную запись в приложение</h2>
                  <p className="muted">
                    Введите ключ настройки вручную или откройте ссылку в приложении-аутентификаторе.
                  </p>
                  <Field
                    label="Ключ настройки"
                    name="setup-key"
                    value={enrollment.secret}
                    readOnly
                    autoComplete="off"
                  />
                  <a className="pill" href={enrollment.provisioningUri}>
                    Открыть в приложении
                  </a>
                </div>
              ) : (
                <p role="status">Код придёт на рабочую почту. Введите его ниже.</p>
              )}
              <Field
                label="Код подтверждения"
                name="code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
              />
              <footer>
                <Link className="pill" to="/account/settings">
                  Отмена
                </Link>
                <Button type="submit" className="primary" disabled={pending}>
                  {pending ? 'Подождите…' : 'Подтвердить'}
                </Button>
              </footer>
            </>
          )}
          {error && <ErrorState error={error} />}
        </form>
      )}
    </section>
  );
}

export function SecurityFactors({ factors }: { factors: Schema<'MfaFactorResponse'>[] }) {
  return (
    <>
      {(['EMAIL', 'TOTP'] as const)
        .filter((method) => method !== 'EMAIL' || EMAIL_ENABLED)
        .map((method) => {
          const factor = factors.find((item) => item.method === method);
          return (
            <div className="permission-row" key={method}>
              <span>{method === 'EMAIL' ? 'Электронная почта' : 'Приложение-аутентификатор'}</span>
              {factor ? (
                <Status value={factor.status} />
              ) : (
                <Link
                  className="pill"
                  to="/account/security/$method"
                  params={{ method: method === 'EMAIL' ? 'email' : 'totp' }}
                >
                  Подключить
                </Link>
              )}
            </div>
          );
        })}
    </>
  );
}
